import React, {memo, useContext, useEffect, useMemo} from 'react'
import {useRouteMatch} from '@modifi/router'
import {useTranslation} from '@modifi/plugin-i18n'
import {useCountrySpecificTranslationKey} from '@modifi/plugin-onboarding'

import {NavigationItemContainer, MenuLink, Label, LabelWrapper, SelectOption} from './styles'
import {NavigationComponents} from '../types'
import {NavigationItemEntry} from '../../../../../features/navigation/types'
import {ActiveItemNavigationContext} from '../../../../../layouts/context-provider'

type NavigationItemProps = Pick<
  NavigationItemEntry,
  | 'sectionName'
  | 'label'
  | 'translationKey'
  | 'route'
  | 'skipIfNoSubNavigation'
  | 'icon'
  | 'actions'
  | 'permissions'
  | 'linkProps'
> & {
  level: number
  hasSubNavigation: boolean
  isNavItemVisible: boolean
  components: NavigationComponents
  init?: (sectionName: string) => void
  childRoutes?: string[]
}

const Item: React.FC<NavigationItemProps> = ({
  sectionName,
  label,
  translationKey,
  icon: LabelIcon,
  route,
  actions,
  hasSubNavigation,
  isNavItemVisible,
  level,
  components,
  skipIfNoSubNavigation = false,
  init,
  linkProps,
  childRoutes = [],
}) => {
  const {t} = useTranslation('charon')
  const countrySpecificTranslationKey = useCountrySpecificTranslationKey()

  const {updateActiveNavItem} = useContext(ActiveItemNavigationContext)

  const match = useRouteMatch({
    path: route,
    exact: true,
  })
  const matchCreateItem = useRouteMatch({
    path: `${route}/create`,
    exact: true,
  })
  const matchItem = useRouteMatch({
    path: `${route}/`,
    exact: false,
  })

  const {path: currentPath} = useRouteMatch()
  const childMatch = !!childRoutes?.find(path => currentPath.includes(path))

  useEffect(() => {
    if (sectionName && init) {
      init(sectionName)
    }
  }, [init, sectionName])

  const isAllowedToSee = true
  const isAllowedToClick = true

  const classNames = []
  if (match || matchCreateItem || (matchItem && !childMatch)) {
    classNames.push('active')
    sectionName && updateActiveNavItem(sectionName)
  }
  if (childMatch || match) classNames.push('open')
  if (!isAllowedToClick) classNames.push('disabled')
  const finalClassName = classNames.join(' ')

  let labelContent = ''
  if (translationKey && typeof translationKey === 'string') {
    labelContent = t(countrySpecificTranslationKey(translationKey))
  } else if (translationKey && Array.isArray(translationKey) && translationKey.length > 0) {
    const [key, ...data] = translationKey
    labelContent = t(countrySpecificTranslationKey(key), ...data)
  } else {
    labelContent = label || ''
  }

  return useMemo(
    () =>
      (!skipIfNoSubNavigation || hasSubNavigation) && isAllowedToSee && isNavItemVisible ? (
        <NavigationItemContainer className={finalClassName} root={level === 0}>
          <MenuLink
            to={route}
            disabled={!isAllowedToClick}
            data-ident={linkProps?.ident || 'sub-section-menu-item'}
            data-target={linkProps?.target || sectionName}
            hasChildMatch={childMatch}
          >
            <LabelWrapper level={level}>
              <components.status sectionName={sectionName} />
              <Label hasIcon={!!LabelIcon}>
                {LabelIcon && <LabelIcon />}
                {labelContent}
              </Label>
            </LabelWrapper>
            {actions?.delete && <actions.delete {...(actions?.params || null)} />}
            {hasSubNavigation && <SelectOption />}
          </MenuLink>
          {sectionName && childMatch && (
            <components.navigationContainer
              sectionName={sectionName}
              components={components}
              level={level + 1}
            />
          )}
        </NavigationItemContainer>
      ) : null,
    [
      skipIfNoSubNavigation,
      hasSubNavigation,
      isAllowedToSee,
      finalClassName,
      level,
      route,
      isAllowedToClick,
      components,
      sectionName,
      LabelIcon,
      labelContent,
      actions,
      childMatch,
      linkProps,
      isNavItemVisible,
    ]
  )
}

export default memo(Item)
